import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import { TextField } from '@mui/material';
import { DateTime } from 'luxon';
import { timezoneAbbreviation, zeroPad } from '../../utils/parsing';

import 'react-day-picker/lib/style.css';
import './picker.css';

export function daysAgo(days) {
  const dt = DateTime.now();
  return dt.minus({ days }).toJSDate();
}

function mergeTime(sourceDate, time, buildingTz) {
  if (time == null) {
    return sourceDate;
  }
  const d = DateTime.fromJSDate(sourceDate, { zone: buildingTz });
  let hours;
  let minutes;
  if (typeof time === 'object') {
    // time is a Date in the browser local time which needs to be interprted
    // in building local time
    const dt = DateTime.fromJSDate(time, { zone: buildingTz });
    hours = dt.hour;
    minutes = dt.minute;
  } else {
    // time is a 24hr string like '13:30'
    [hours, minutes] = time.split(':');
  }
  if (hours == null || Number.isNaN(hours) || minutes == null || Number.isNaN(minutes)) {
    return d.toJSDate();
  }
  return d
    .set({
      hours,
      minutes,
      seconds: 0,
      milliseconds: 0,
    })
    .toJSDate();
}

/**
 * Return the date in accordance to the timezone (since Date forces locale timezone)
 */
function getEffectiveDateValue(date, timezone) {
  const d = DateTime.fromJSDate(date, { zone: timezone });
  const millisOffset = (date.getTimezoneOffset() - d.offset) * 60 * 1000;
  return new Date(date.getTime() + millisOffset);
}

/**
 * Return the time in accordance to the timezone (since Date forces locale timezone)
 */
function getEffectiveTimeValue(date, timezone) {
  const d = DateTime.fromJSDate(date).setZone(timezone);
  return `${zeroPad(d.hour)}:${zeroPad(d.minute)}`; // 13:03
}

/**
 * Controlled component for choosing a date range
 */
const RangePicker = ({ from, to, timezone, error, onChange }) => {
  const handleFromChange = (newFrom) =>
    onChange({ from: newFrom, to: to > newFrom ? to : new Date() });
  const handleToChange = (newTo) => onChange({ from, to: newTo });
  const modifiers = { start: from, end: to };
  const toInput = useRef(null);
  return (
    <span className={`RangePicker ${error ? 'error-state' : ''}`}>
      <DayPickerInput
        value={getEffectiveDateValue(from, timezone)}
        placeholder="From"
        dayPickerProps={{
          selectedDays: [from, { from, to }],
          disabledDays: { after: new Date() },
          toMonth: to,
          modifiers,
          numberOfMonths: 2,
          onDayClick: () => toInput.current.getInput().focus(),
        }}
        onDayChange={(d) => handleFromChange(mergeTime(d, from, timezone))}
      />
      <TextField
        className="RangePicker-Time"
        type="time"
        value={getEffectiveTimeValue(from, timezone)}
        inputProps={{
          className: 'RangePicker-Time',
          step: 60,
        }}
        onChange={(e) => handleFromChange(mergeTime(from, e.target.value, timezone))}
      />
      {timezoneAbbreviation(timezone)} —{' '}
      <span className="InputFromTo-to">
        <DayPickerInput
          ref={toInput}
          value={getEffectiveDateValue(to, timezone)}
          placeholder="To"
          dayPickerProps={{
            selectedDays: [from, { from, to }],
            disabledDays: { before: from, after: new Date() },
            modifiers,
            month: from,
            fromMonth: from,
            numberOfMonths: 2,
            todayButton: 'Today',
          }}
          onDayChange={(d) => handleToChange(mergeTime(d, to, timezone))}
        />
        <TextField
          className="RangePicker-Time"
          type="time"
          value={getEffectiveTimeValue(to, timezone)}
          inputProps={{
            className: 'RangePicker-Time',
            step: 60,
          }}
          onChange={(e) => handleToChange(mergeTime(to, e.target.value, timezone))}
        />
        {timezoneAbbreviation(timezone)}
      </span>
    </span>
  );
};

RangePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  from: PropTypes.instanceOf(Date),
  to: PropTypes.instanceOf(Date),
  timezone: PropTypes.string,
  error: PropTypes.bool,
};

RangePicker.defaultProps = {
  from: daysAgo(1),
  to: new Date(),
  timezone: 'UTC',
  error: false,
};

export default RangePicker;
